import { supabase } from "@/integrations/supabase/client";

export interface DashboardMetrics {
  totalMailboxes: number;
  activeMailboxes: number;
  totalTemplates: number;
  totalRules: number;
  activeRules: number;
  recentEventsCount: number;
}

export interface RecentActivity {
  id: string;
  created_at: string;
  status: string;
  processed_at: string | null;
  mailbox_email?: string;
  rule_name?: string;
}

export interface OnboardingProgress {
  hasMailboxes: boolean;
  hasTemplates: boolean;
  hasRules: boolean;
  hasEvents: boolean;
}

// Combined function to fetch both metrics and onboarding progress in one call
export const fetchDashboardData = async (tenantId: string): Promise<{
  metrics: DashboardMetrics;
  onboardingProgress: OnboardingProgress;
}> => {
  try {
    const { data, error } = await supabase.rpc('get_dashboard_metrics', {
      p_tenant_id: tenantId
    });

    if (error) throw error;

    const result = data as any;
    return {
      metrics: result.metrics,
      onboardingProgress: result.onboardingProgress
    };
  } catch (error) {
    console.error("Error fetching dashboard data:", error);
    throw error;
  }
};

// Legacy function for backward compatibility
export const fetchDashboardMetrics = async (tenantId: string): Promise<DashboardMetrics> => {
  const { metrics } = await fetchDashboardData(tenantId);
  return metrics;
};

export const fetchRecentActivity = async (tenantId: string): Promise<RecentActivity[]> => {
  try {
    const { data, error } = await supabase
      .from("email_events")
      .select(`
        id,
        status,
        created_at,
        processed_at,
        mailboxes(email)
      `)
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: false })
      .limit(10);

    if (error) throw error;

    return (data || []).map((event: any) => ({
      id: event.id,
      created_at: event.created_at,
      status: event.status,
      processed_at: event.processed_at,
      mailbox_email: event.mailboxes?.email
    }));
  } catch (error) {
    console.error("Error fetching recent activity:", error);
    throw error;
  }
};

// Legacy function for backward compatibility  
export const fetchOnboardingProgress = async (tenantId: string): Promise<OnboardingProgress> => {
  const { onboardingProgress } = await fetchDashboardData(tenantId);
  return onboardingProgress;
};